import { computed, Signal, signal, WritableSignal } from '@angular/core';

export enum PendingChangesSaveStatus {
  Idle = 'idle',
  Pending = 'pending',
  Saving = 'saving',
  Saved = 'saved',
  Error = 'error',
}

export interface PendingChangesSaveStatusOptions {
  readonly saved_message_duration_ms?: number;
}

export interface PendingChangesSaveStatusState {
  readonly status: WritableSignal<PendingChangesSaveStatus>;
  readonly show_saved_message: WritableSignal<boolean>;
  readonly label: Signal<string>;
  readonly icon: Signal<string>;
  readonly is_pending: Signal<boolean>;
  readonly is_saving: Signal<boolean>;
  readonly is_saved: Signal<boolean>;
  readonly is_error: Signal<boolean>;
  markPending: () => void;
  markSaving: () => void;
  markSaved: () => void;
  markError: () => void;
  reset: () => void;
  hideSavedMessage: () => void;
  destroy: () => void;
}

export function createPendingChangesSaveStatus(options: PendingChangesSaveStatusOptions = {}): PendingChangesSaveStatusState {
  const saved_message_duration_ms: number = options.saved_message_duration_ms ?? 5000;
  const status: WritableSignal<PendingChangesSaveStatus> = signal<PendingChangesSaveStatus>(PendingChangesSaveStatus.Idle);
  const show_saved_message: WritableSignal<boolean> = signal<boolean>(false);
  let saved_message_timeout_id: ReturnType<typeof setTimeout> | null = null;

  const clearSavedMessageTimeout = (): void => {
    if (saved_message_timeout_id === null) {
      return;
    }

    clearTimeout(saved_message_timeout_id);
    saved_message_timeout_id = null;
  };

  const hideSavedMessage = (): void => {
    clearSavedMessageTimeout();
    show_saved_message.set(false);
  };

  const scheduleHideSavedMessage = (): void => {
    clearSavedMessageTimeout();
    saved_message_timeout_id = setTimeout((): void => {
      show_saved_message.set(false);
      saved_message_timeout_id = null;
    }, saved_message_duration_ms);
  };

  const label: Signal<string> = computed<string>(() => {
    switch (status()) {
      case PendingChangesSaveStatus.Pending:
        return 'Cambios pendientes de guardar...';
      case PendingChangesSaveStatus.Saving:
        return 'Se está guardando...';
      case PendingChangesSaveStatus.Saved:
        return show_saved_message() ? 'Se ha guardado.' : '';
      case PendingChangesSaveStatus.Error:
        return 'Error al guardar.';
      case PendingChangesSaveStatus.Idle:
        return '';
    }
  });

  const icon: Signal<string> = computed<string>(() => {
    switch (status()) {
      case PendingChangesSaveStatus.Pending:
        return 'hourglass_bottom';
      case PendingChangesSaveStatus.Saving:
        return 'cloud_sync';
      case PendingChangesSaveStatus.Error:
        return 'warning';
      case PendingChangesSaveStatus.Idle:
      case PendingChangesSaveStatus.Saved:
        return 'cloud_done';
    }
  });

  const is_pending: Signal<boolean> = computed<boolean>(() => status() === PendingChangesSaveStatus.Pending);
  const is_saving: Signal<boolean> = computed<boolean>(() => status() === PendingChangesSaveStatus.Saving);
  const is_saved: Signal<boolean> = computed<boolean>(() => status() === PendingChangesSaveStatus.Saved);
  const is_error: Signal<boolean> = computed<boolean>(() => status() === PendingChangesSaveStatus.Error);

  const markPending = (): void => {
    hideSavedMessage();
    status.set(PendingChangesSaveStatus.Pending);
  };

  const markSaving = (): void => {
    hideSavedMessage();
    status.set(PendingChangesSaveStatus.Saving);
  };

  const markSaved = (): void => {
    status.set(PendingChangesSaveStatus.Saved);
    show_saved_message.set(true);
    scheduleHideSavedMessage();
  };

  const markError = (): void => {
    hideSavedMessage();
    status.set(PendingChangesSaveStatus.Error);
  };

  const reset = (): void => {
    hideSavedMessage();
    status.set(PendingChangesSaveStatus.Idle);
  };

  const pending_changes_save_status_state: PendingChangesSaveStatusState = {
    status,
    show_saved_message,
    label,
    icon,
    is_pending,
    is_saving,
    is_saved,
    is_error,
    markPending,
    markSaving,
    markSaved,
    markError,
    reset,
    hideSavedMessage,
    destroy: clearSavedMessageTimeout,
  };

  return pending_changes_save_status_state;
}
